import {
  appendQuery,
  buildCellServiceUrl,
  type CellServiceQuery,
} from './cell-session';
import { isAbortError, withTokenAuthRecovery } from './cors-auth-recovery';

// =============================================================================
// Client base
// =============================================================================
//
// Direct browser-side calls to ``https://{cellId}.api.tranquility.ai``
// ``/audit-service/v1/*``. The CSAM audit-log query endpoints used to live on
// provenance-service (``/provenance-service/v1/csam-audit/*``); they were moved
// to the dedicated audit-service, so the CSAM audit viewer now calls into this
// module using the access token resolved by ``useCellServiceSession``.

/** Path prefix for every audit-service v1 endpoint. */
const API_PREFIX = '/audit-service/v1';

/**
 * Error thrown by every direct-CORS call to audit-service. Carries the
 * upstream HTTP status (when available) and the raw response body for
 * downstream loggers.
 */
export class AuditApiError extends Error {
  public readonly status: number | null;
  public readonly detail: string | null;

  constructor(message: string, status: number | null = null, detail: string | null = null) {
    super(message);
    this.name = 'AuditApiError';
    this.status = status;
    this.detail = detail;
  }
}

/**
 * Build a fully-qualified audit-service URL for a given cell.
 *
 * ``path`` is the route *after* ``/audit-service/v1`` (e.g.
 * ``"/csam-audit/queries"``). A leading slash is optional.
 */
export function buildAuditUrl(cellId: string, path: string): string {
  return buildCellServiceUrl(cellId, API_PREFIX, path);
}

/** Query map: scalar values are emitted once; arrays are emitted as repeated keys. */
export type AuditQuery = CellServiceQuery;

type AuditFetchArgs = {
  /** ``custom:cell_id`` claim, used to build the per-cell API host. */
  cellId: string;
  /**
   * Real Cognito access token (``token_use: "access"``). In this app that's
   * ``AuthContext.realAccessToken`` / ``features.tokens.rawAccessToken`` —
   * NOT ``AuthContext.accessToken`` (which is the ID token for legacy BFF
   * cookie reasons and would be rejected by the Lambda Authorizer).
   */
  accessToken: string;
  /** Path under ``/audit-service/v1`` (e.g. ``"/csam-audit/queries/abc"``). */
  path: string;
  /** Optional query map; arrays produce repeated keys. */
  query?: AuditQuery;
  signal?: AbortSignal;
};

function assertSession(cellId: string, accessToken: string): void {
  if (!cellId) {
    throw new AuditApiError('Missing cellId (custom:cell_id claim) on the ID token.');
  }
  if (!accessToken) {
    throw new AuditApiError('Missing access token.');
  }
}

/**
 * Generic GET helper for audit-service. Validates the session inputs, issues
 * the request, and either returns parsed JSON typed as ``T`` or throws an
 * ``AuditApiError``.
 */
export async function auditFetch<T>(args: AuditFetchArgs): Promise<T> {
  const { cellId, path, query, signal } = args;
  assertSession(cellId, args.accessToken);

  const url = appendQuery(buildAuditUrl(cellId, path), query);

  return withTokenAuthRecovery(args.accessToken, async (accessToken) => {
    let res: Response;
    try {
      res = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        signal,
      });
    } catch (error) {
      // A caller-cancelled request is not a failure. Wrapping it would match
      // isLikelyCorsOrNetworkFailure and trigger a token refresh for a cancel.
      if (isAbortError(error)) throw error;
      throw new AuditApiError(
        `Network error calling audit-service ${path}: ${(error as Error).message}`,
      );
    }

    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new AuditApiError(
        `audit-service ${path} returned ${res.status}`,
        res.status,
        detail || null,
      );
    }

    return (await res.json().catch(() => null)) as T;
  });
}

type AuditPostArgs = {
  cellId: string;
  accessToken: string;
  /** Path under ``/audit-service/v1`` (e.g. ``"/csam-audit/queries"``). */
  path: string;
  body: unknown;
  signal?: AbortSignal;
};

/**
 * Generic POST helper for audit-service. Mirrors ``auditFetch`` but sends a
 * JSON body and uses the POST method. Throws ``AuditApiError`` on network or
 * HTTP errors.
 */
export async function auditPost<T>(args: AuditPostArgs): Promise<T> {
  const { cellId, accessToken, path, body, signal } = args;
  assertSession(cellId, accessToken);

  const url = buildAuditUrl(cellId, path);

  return withTokenAuthRecovery(accessToken, async (token) => {
    let res: Response;
    try {
      res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
        signal,
      });
    } catch (error) {
      // A caller-cancelled request is not a failure. Wrapping it would match
      // isLikelyCorsOrNetworkFailure and trigger a token refresh for a cancel.
      if (isAbortError(error)) throw error;
      throw new AuditApiError(
        `Network error calling audit-service ${path}: ${(error as Error).message}`,
      );
    }

    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new AuditApiError(
        `audit-service ${path} returned ${res.status}`,
        res.status,
        detail || null,
      );
    }

    return (await res.json().catch(() => null)) as T;
  });
}

// =============================================================================
// CSAM audit-log endpoints
// =============================================================================
//
// Async start+poll query over the per-tenant WORM audit trail:
//   POST /csam-audit/queries       -> { queryId }
//   GET  /csam-audit/queries/{id}  -> { status, rows, nextToken }
// The backend resolves the tenant from the caller and always injects the
// tenant + date bounds into the Athena SQL — the client only picks a
// dimension/value and a date window.

/** Which axis the audit log is filtered on. */
export type CsamAuditDimension = 'file' | 'user' | 'case' | 'tenant';

/** Poll status for a started audit query. */
export type CsamAuditQueryStatus = 'RUNNING' | 'SUCCEEDED' | 'FAILED';

/** Request body for ``POST /csam-audit/queries``. */
export interface StartCsamAuditQueryArgs {
  cellId: string;
  accessToken: string;
  dimension: CsamAuditDimension;
  /** Required for file/user/case; ignored for the tenant-wide dimension. */
  value?: string | null;
  /** Inclusive start date, ``YYYY-MM-DD`` (UTC). */
  from?: string | null;
  /** Inclusive end date, ``YYYY-MM-DD`` (UTC). */
  to?: string | null;
  pageSize?: number;
  signal?: AbortSignal;
}

/** Response from ``POST /csam-audit/queries``. */
export interface StartCsamAuditQueryResponse {
  queryId: string;
}

/**
 * One audit row. All values are strings (Athena result cells). Columns mirror
 * ``tai_data.domain.audit.csam.CsamAuditEvent`` flattened: ``event_id``,
 * ``occurred_at``, ``action``, ``variant``, ``user_id``, ``email``,
 * ``source_ip``, ``case_id``, ``s3_uri``, ``detection_type``,
 * ``finding_status_from``, ``finding_status_to``, ``result``.
 */
export type CsamAuditRow = Record<string, string>;

/** Response from ``GET /csam-audit/queries/{id}``. */
export interface CsamAuditQueryResult {
  status: CsamAuditQueryStatus;
  rows: CsamAuditRow[];
  nextToken: string | null;
  error?: string | null;
}

/** Direct-CORS POST to start an audit query. Returns the Athena query id. */
export async function startCsamAuditQuery(
  args: StartCsamAuditQueryArgs,
): Promise<StartCsamAuditQueryResponse> {
  const { cellId, accessToken, dimension, value, from, to, pageSize, signal } = args;
  const body: Record<string, unknown> = { dimension };
  if (value != null && value !== '') body.value = value;
  if (from) body.from = from;
  if (to) body.to = to;
  if (typeof pageSize === 'number') body.pageSize = pageSize;

  return auditPost<StartCsamAuditQueryResponse>({
    cellId,
    accessToken,
    path: '/csam-audit/queries',
    body,
    signal,
  });
}

type FetchCsamAuditQueryResultArgs = {
  cellId: string;
  accessToken: string;
  queryId: string;
  nextToken?: string | null;
  signal?: AbortSignal;
};

/** Direct-CORS GET to poll a started audit query and read a page of rows. */
export async function fetchCsamAuditQueryResult(
  args: FetchCsamAuditQueryResultArgs,
): Promise<CsamAuditQueryResult> {
  const { cellId, accessToken, queryId, nextToken, signal } = args;
  const query: AuditQuery = {};
  if (nextToken) query.nextToken = nextToken;

  const body = await auditFetch<unknown>({
    cellId,
    accessToken,
    path: `/csam-audit/queries/${encodeURIComponent(queryId)}`,
    query,
    signal,
  });

  if (!body || typeof body !== 'object') {
    throw new AuditApiError('audit-service /csam-audit/queries returned an unexpected body.');
  }
  const parsed = body as CsamAuditQueryResult;
  return {
    status: parsed.status,
    rows: Array.isArray(parsed.rows) ? parsed.rows : [],
    nextToken: parsed.nextToken ?? null,
    error: parsed.error ?? null,
  };
}

// =============================================================================
// Query keys
// =============================================================================

/**
 * Hierarchical TanStack Query key factory for every audit-service resource.
 * Use the most specific key in each hook so partial invalidation
 * (``queryClient.invalidateQueries({ queryKey: auditKeys.csamAudit() })``) is
 * straightforward.
 */
export const auditKeys = {
  all: ['audit'] as const,
  csamAudit: () => [...auditKeys.all, 'csam-audit'] as const,
  csamAuditQuery: (
    cellId: string | null,
    params: {
      dimension: CsamAuditDimension;
      value: string | null;
      from: string | null;
      to: string | null;
      pageSize: number;
    },
  ) => [...auditKeys.csamAudit(), 'query', cellId, params] as const,
};
